<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSessionStore } from "./stores/session";
import LoginView from "./views/LoginView.vue";

const session = useSessionStore();
const route = useRoute();
const router = useRouter();
const dark = ref(localStorage.getItem("aimarket.admin.theme") === "dark");
const mobileNav = ref(false);

const navigation = [
  { to: "/", label: "Обзор", icon: "bi-grid-1x2" },
  { to: "/store", label: "Магазин", icon: "bi-shop" },
  { to: "/syncs", label: "Синхронизации", icon: "bi-arrow-repeat" },
  { to: "/search", label: "Поисковый индекс", icon: "bi-bezier2" },
  { to: "/usage", label: "Расходы AI", icon: "bi-stars" },
  { to: "/events", label: "События и ошибки", icon: "bi-activity" },
  { to: "/audit", label: "Аудит", icon: "bi-shield-check" },
];
const pageTitle = computed(() => String(route.meta.title ?? "Control Center"));

function applyTheme(): void {
  document.documentElement.dataset.bsTheme = dark.value ? "dark" : "light";
  localStorage.setItem("aimarket.admin.theme", dark.value ? "dark" : "light");
}
function toggleTheme(): void {
  dark.value = !dark.value;
  applyTheme();
}
async function logout(): Promise<void> {
  await session.logout();
  await router.push("/");
}
onMounted(async () => {
  applyTheme();
  await session.initialize();
});
</script>

<template>
  <div v-if="!session.initialized" class="boot-screen">
    <div class="brand-mark"><i class="bi bi-stars" /></div>
    <div class="spinner-border spinner-border-sm" role="status" aria-label="Загрузка" />
  </div>
  <LoginView v-else-if="!session.principal" />
  <div v-else class="admin-shell">
    <aside class="sidebar" :class="{ 'is-open': mobileNav }">
      <div class="brand">
        <div class="brand-mark"><i class="bi bi-stars" /></div>
        <div><strong>AI Market</strong><span>Control Center</span></div>
      </div>
      <nav aria-label="Основная навигация">
        <RouterLink v-for="item in navigation" :key="item.to" :to="item.to" class="nav-item" @click="mobileNav = false">
          <i class="bi" :class="item.icon" /><span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <div class="sidebar-footer">
        <div class="actor-avatar">{{ session.principal.displayName.slice(0, 1) }}</div>
        <div class="actor-copy"><strong>{{ session.principal.displayName }}</strong><span>{{ session.principal.role }}</span></div>
        <button class="icon-button" title="Выйти" @click="logout"><i class="bi bi-box-arrow-right" /></button>
      </div>
    </aside>
    <main class="main-area">
      <header class="topbar">
        <button class="icon-button d-lg-none" aria-label="Открыть меню" @click="mobileNav = !mobileNav"><i class="bi bi-list" /></button>
        <div><p class="eyebrow">Операционный центр</p><h1>{{ pageTitle }}</h1></div>
        <div class="topbar-actions">
          <label class="tenant-select">
            <span>Магазин</span>
            <select :value="session.tenantId" @change="session.selectTenant(($event.target as HTMLSelectElement).value)">
              <option v-for="tenant in session.tenants" :key="tenant.tenant_id" :value="tenant.tenant_id">{{ tenant.display_name }}</option>
            </select>
          </label>
          <button class="icon-button" :title="dark ? 'Светлая тема' : 'Тёмная тема'" @click="toggleTheme"><i class="bi" :class="dark ? 'bi-sun' : 'bi-moon-stars'" /></button>
        </div>
      </header>
      <div class="content-area"><RouterView :key="`${route.fullPath}:${session.tenantId}`" /></div>
    </main>
    <button v-if="mobileNav" class="nav-backdrop d-lg-none" aria-label="Закрыть меню" @click="mobileNav = false" />
  </div>
</template>
